import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useLessons } from "../data/LessonsContext";
import useLessonBlocks from "../data/useLessonBlocks";
import useProgress from "../data/useProgress";
import { COMPLETED } from "../data/progressStore";
import Markdown from "../components/Markdown";
import ConfirmSheet from "../components/ConfirmSheet";
import ReleaseCard from "../components/ReleaseCard";
import { CloseIcon, EyeIcon } from "../components/Icons";
import {
  LoadingCards,
  NotAvailable,
  ProblemMessage,
} from "../components/Status";
import ui from "../components/ui.module.css";

const Section = ({ title, children }) =>
  children ? (
    <section className={`${ui.card} ${ui.section}`}>
      <h2>{title}</h2>
      <Markdown className={ui.content}>{children}</Markdown>
    </section>
  ) : null;

const LessonPlayer = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const lessons = useLessons();
  const lesson = lessons.findLesson(slug);
  const blocks = useLessonBlocks(lesson?.id);
  const progress = useProgress();
  const headingRef = useRef(null);
  const [confirming, setConfirming] = useState(false);

  const record = lesson ? progress.get(lesson.id) : null;
  const total = blocks.blocks.length;
  const step = Math.min(record?.step ?? 0, Math.max(total - 1, 0));

  useEffect(() => {
    headingRef.current?.focus();
  }, [step, total]);

  const closeSheet = useCallback(() => setConfirming(false), []);

  const frame = (children) => <main className={ui.page}>{children}</main>;

  if (lessons.status === "loading") return frame(<LoadingCards count={1} />);
  if (lessons.status === "error") {
    return frame(
      <ProblemMessage problem={lessons.problem} onRetry={lessons.retry} autoRetrying={lessons.autoRetrying} />
    );
  }
  if (!lesson) return frame(<NotAvailable />);

  const overview = `/lesson/${lesson.slug}`;
  if (!record || record.status === COMPLETED) {
    return <Navigate to={overview} replace />;
  }
  if (blocks.status === "loading") {
    return frame(<LoadingCards label="Getting the lesson ready…" count={1} />);
  }
  if (blocks.status === "error") {
    return frame(
      <>
        <ProblemMessage problem={blocks.problem} onRetry={blocks.retry} autoRetrying={blocks.autoRetrying} />
        <Link to={overview} className={ui.textButton}>
          Back to lesson
        </Link>
      </>
    );
  }
  if (total === 0) return <Navigate to={overview} replace />;

  const block = blocks.blocks[step];
  const isLast = step === total - 1;
  const goTo = (next) =>
    progress.setStep(lesson.id, next, blocks.blocks[next]?.sort ?? next);
  const complete = () => {
    progress.complete(lesson.id, block.sort ?? step);
    navigate(`${overview}/done`, { replace: true });
  };

  return (
    <>
      <header className={ui.header}>
        <div
          className={`container ${ui.headerInner} ${ui.stack}`}
          style={{ gap: "0.25rem", paddingTop: "0.5rem", paddingBottom: "0.75rem" }}
        >
          <div className={ui.row}>
            <Link
              to={overview}
              className={ui.textButton}
              aria-label="Close lesson. Your place is saved."
            >
              <CloseIcon />
              Close
            </Link>
            <span className={ui.eyebrow} aria-live="polite">
              Step {step + 1} of {total}
            </span>
          </div>
          <progress
            className={ui.progress}
            value={step + 1}
            max={total}
            aria-label={`Step ${step + 1} of ${total}`}
          />
        </div>
      </header>

      <main className={ui.page} id="main">
        <div className={ui.stack}>
          <p className={ui.eyebrow}>
            {[block.kind, block.minutes && `${block.minutes} min`]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <h1 className={ui.title} ref={headingRef} tabIndex={-1}>
            {block.title || block.kind || `Step ${step + 1}`}
          </h1>
          <p className={ui.meta}>{lesson.title}</p>
        </div>

        <ReleaseCard
          key={block.id}
          lessonId={lesson.id}
          blockId={block.id}
          record={record}
        />
        <Section title="What you do">{block.teacherActions}</Section>
        <Section title="What learners do">{block.learnerActions}</Section>
        <Section title="You'll need">{block.resources}</Section>
        {block.lookFor && (
          <section className={`${ui.observe} ${ui.section}`}>
            <h2 className={ui.metaItem}>
              <EyeIcon />
              What to look for
            </h2>
            <Markdown className={ui.content}>{block.lookFor}</Markdown>
          </section>
        )}
      </main>

      <div className={ui.actionBar}>
        <div className={ui.actionBarInner}>
          <button
            type="button"
            className={`${ui.button} ${ui.secondary}`}
            onClick={() => goTo(step - 1)}
            disabled={step === 0}
          >
            Back
          </button>
          {isLast ? (
            <button
              type="button"
              className={`${ui.button} ${ui.primary} ${ui.grow}`}
              onClick={() => setConfirming(true)}
            >
              Complete lesson
            </button>
          ) : (
            <button
              type="button"
              className={`${ui.button} ${ui.primary} ${ui.grow}`}
              onClick={() => goTo(step + 1)}
            >
              Next
            </button>
          )}
        </div>
      </div>

      {confirming && (
        <ConfirmSheet
          title="Finish this lesson?"
          confirmLabel="Complete lesson"
          cancelLabel="Keep teaching"
          onConfirm={complete}
          onCancel={closeSheet}
        >
          <p>“{lesson.title}” will be marked as completed.</p>
        </ConfirmSheet>
      )}
    </>
  );
};

export default LessonPlayer;
